import type { App } from './app.ts'
import { createStore } from './store.ts'

/**
 * Whether today's letter has already gone out, once, shared (#102, Q18.3).
 *
 * Two screens need the same fact and neither owns it. The compose screen needs
 * it to know whether to offer the sheet at all; the island needs it to know
 * whether a spent day should point at the letter, or say nothing because the
 * letter is already in the post. Asking twice would be two requests for one
 * answer, and two chances to disagree.
 *
 * The fact itself is `lettersSent` on `GET /api/day/:day/share.json`, which is
 * exactly this and is already in the read model (`player_islands.letter_sent`).
 * One day is held at a time, because only today's letter is ever in question;
 * asking about another day replaces it.
 */

export type LetterSentState = {
  /** The day the answer is about, or null before anything has been asked. */
  day: string | null
  sent: boolean
  loaded: boolean
}

export const letterSentStore = createStore<LetterSentState>({
  day: null,
  sent: false,
  loaded: false,
})

/** What is known about `day`, which is `false` until something says otherwise. */
export function letterSentOn(day: string): boolean {
  const state = letterSentStore.get()
  return state.loaded && state.day === day && state.sent
}

/** True once the answer for `day` is in, so a caller can wait rather than guess. */
export function letterSentKnown(day: string): boolean {
  const state = letterSentStore.get()
  return state.loaded && state.day === day
}

/** Which player the answer is about. Bumped by every clear (#115). */
let generation = 0

/**
 * Ask, unless the answer for this day is already here.
 *
 * A failure leaves the question unanswered rather than asserting a letter has
 * gone: the worse mistake is telling somebody they have written when they have
 * not.
 */
export async function loadLetterSent(app: App, day: string): Promise<void> {
  if (letterSentKnown(day)) return
  const mine = generation
  const card = await app.api.shareCard(day)
  // Not this player's day any more, so not this player's answer (#115).
  if (!card.ok || generation !== mine) return
  letterSentStore.set({ day, sent: card.value.lettersSent > 0, loaded: true })
}

/** The send itself, so neither screen has to ask again to stop offering. */
export function markLetterSent(day: string): void {
  letterSentStore.set({ day, sent: true, loaded: true })
}

/** Forget it when the player changes: the next player's day is not this one's. */
export function clearLetterSent(): void {
  generation += 1
  letterSentStore.reset()
}
